// ---------------------------------------------------------------------------
// Technical Indicators — pure functions operating on OHLCV arrays
// ---------------------------------------------------------------------------

export interface OHLCV {
  time: number; // Unix seconds
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface IndicatorPoint {
  time: number;
  value: number;
}

export interface MACDPoint {
  time: number;
  macd: number;
  signal: number;
  histogram: number;
}

// ---------- Moving Averages ----------

export function sma(data: OHLCV[], period: number): IndicatorPoint[] {
  if (data.length < period) return [];
  const out: IndicatorPoint[] = [];
  let sum = 0;
  for (let i = 0; i < data.length; i++) {
    sum += data[i]!.close;
    if (i >= period) sum -= data[i - period]!.close;
    if (i >= period - 1) {
      out.push({ time: data[i]!.time, value: sum / period });
    }
  }
  return out;
}

export function ema(data: OHLCV[], period: number): IndicatorPoint[] {
  if (data.length < period) return [];
  const k = 2 / (period + 1);
  const out: IndicatorPoint[] = [];
  // Seed with SMA of first `period` closes
  let prev = 0;
  for (let i = 0; i < period; i++) prev += data[i]!.close;
  prev /= period;
  out.push({ time: data[period - 1]!.time, value: prev });
  for (let i = period; i < data.length; i++) {
    prev = data[i]!.close * k + prev * (1 - k);
    out.push({ time: data[i]!.time, value: prev });
  }
  return out;
}

// ---------- RSI (Wilder's smoothing) ----------

export function rsi(data: OHLCV[], period = 14): IndicatorPoint[] {
  if (data.length < period + 1) return [];
  const out: IndicatorPoint[] = [];
  let avgGain = 0;
  let avgLoss = 0;
  for (let i = 1; i <= period; i++) {
    const diff = data[i]!.close - data[i - 1]!.close;
    if (diff >= 0) avgGain += diff;
    else avgLoss -= diff;
  }
  avgGain /= period;
  avgLoss /= period;
  const rs1 = avgLoss === 0 ? 100 : avgGain / avgLoss;
  out.push({ time: data[period]!.time, value: 100 - 100 / (1 + rs1) });

  for (let i = period + 1; i < data.length; i++) {
    const diff = data[i]!.close - data[i - 1]!.close;
    const gain = diff > 0 ? diff : 0;
    const loss = diff < 0 ? -diff : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    out.push({ time: data[i]!.time, value: 100 - 100 / (1 + rs) });
  }
  return out;
}

// ---------- MACD ----------

export function macd(
  data: OHLCV[],
  fast = 12,
  slow = 26,
  signal = 9,
): MACDPoint[] {
  if (data.length < slow + signal) return [];
  const fastEma = ema(data, fast);
  const slowEma = ema(data, slow);
  // Align by time
  const slowMap = new Map(slowEma.map((p) => [p.time, p.value]));
  const macdLine: IndicatorPoint[] = [];
  for (const f of fastEma) {
    const s = slowMap.get(f.time);
    if (s != null) macdLine.push({ time: f.time, value: f.value - s });
  }
  if (macdLine.length < signal) return [];
  // EMA of macdLine for signal
  const k = 2 / (signal + 1);
  let prev = 0;
  for (let i = 0; i < signal; i++) prev += macdLine[i]!.value;
  prev /= signal;
  const signalLine: IndicatorPoint[] = [
    { time: macdLine[signal - 1]!.time, value: prev },
  ];
  for (let i = signal; i < macdLine.length; i++) {
    prev = macdLine[i]!.value * k + prev * (1 - k);
    signalLine.push({ time: macdLine[i]!.time, value: prev });
  }
  const signalMap = new Map(signalLine.map((p) => [p.time, p.value]));
  const out: MACDPoint[] = [];
  for (const m of macdLine) {
    const s = signalMap.get(m.time);
    if (s != null) {
      out.push({ time: m.time, macd: m.value, signal: s, histogram: m.value - s });
    }
  }
  return out;
}

// ---------- Bollinger Bands ----------

export interface BollingerPoint {
  time: number;
  upper: number;
  middle: number;
  lower: number;
}

export function bollingerBands(
  data: OHLCV[],
  period = 20,
  stdDev = 2,
): BollingerPoint[] {
  if (data.length < period) return [];
  const out: BollingerPoint[] = [];
  for (let i = period - 1; i < data.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += data[j]!.close;
    const mean = sum / period;
    let variance = 0;
    for (let j = i - period + 1; j <= i; j++) {
      const diff = data[j]!.close - mean;
      variance += diff * diff;
    }
    const sd = Math.sqrt(variance / period);
    out.push({
      time: data[i]!.time,
      upper: mean + stdDev * sd,
      middle: mean,
      lower: mean - stdDev * sd,
    });
  }
  return out;
}

// ---------- Token-Level Fear & Greed ----------
//
// Composite 0-100 score. Unlike the classic market-wide F&G, this is computed
// per token from its own recent price action. Components:
//   - RSI (14)              — 25%  (weighted: extreme RSI = fear/greed)
//   - Momentum 7d           — 20%  (% change)
//   - Volatility (std/mean) — 20%  (high volatility = fear)
//   - Volume trend          — 15%  (recent vs prior, rising = greed)
//   - Distance from 30d high— 20%  (closer to high = greed)

export interface FearGreedResult {
  score: number; // 0 = extreme fear, 100 = extreme greed
  label: string;
  color: string;
  components: {
    rsi: number;
    momentum: number;
    volatility: number;
    volumeTrend: number;
    drawdown: number;
  };
}

export function computeFearGreed(data: OHLCV[]): FearGreedResult | null {
  if (data.length < 30) return null;

  // --- RSI component (centered around 50, mapped to 0-100) ---
  const rsiSeries = rsi(data, 14);
  const lastRsi = rsiSeries[rsiSeries.length - 1]?.value ?? 50;
  // RSI of 50 = neutral 50, RSI 70+ = greed, RSI 30- = fear
  const rsiScore = Math.max(0, Math.min(100, lastRsi));

  // --- Momentum (7-period % change) ---
  const recent = data[data.length - 1]!.close;
  const past = data[Math.max(0, data.length - 8)]!.close;
  const momentum = ((recent - past) / past) * 100;
  // Map -20% → 0, +20% → 100, linear
  const momentumScore = Math.max(0, Math.min(100, 50 + momentum * 2.5));

  // --- Volatility (std dev of last 14 returns, inverted) ---
  const returns: number[] = [];
  for (let i = data.length - 14; i < data.length; i++) {
    if (i <= 0) continue;
    returns.push((data[i]!.close - data[i - 1]!.close) / data[i - 1]!.close);
  }
  const meanRet = returns.reduce((a, b) => a + b, 0) / returns.length;
  const variance =
    returns.reduce((a, b) => a + (b - meanRet) ** 2, 0) / returns.length;
  const vol = Math.sqrt(variance);
  // Higher vol = more fear. Map 0 → 100 (calm/greed), 0.1 (10% daily) → 0 (extreme fear)
  const volScore = Math.max(0, Math.min(100, 100 - vol * 1000));

  // --- Volume trend (recent 7 vs prior 7) ---
  const recentVol =
    data.slice(-7).reduce((a, b) => a + b.volume, 0) / 7;
  const priorVol =
    data.slice(-14, -7).reduce((a, b) => a + b.volume, 0) / 7;
  const volRatio = priorVol > 0 ? recentVol / priorVol : 1;
  // volRatio 0.5 → 20, 1.0 → 50, 1.5 → 80, 2.0+ → 100
  const volumeTrendScore = Math.max(
    0,
    Math.min(100, 50 + (volRatio - 1) * 50),
  );

  // --- Drawdown from 30-period high ---
  const highWindow = data.slice(-30);
  const high30 = Math.max(...highWindow.map((d) => d.high));
  const drawdown = ((recent - high30) / high30) * 100; // <= 0
  // 0% drawdown = 100 (greed), -30% = 0 (fear)
  const drawdownScore = Math.max(0, Math.min(100, 100 + drawdown * 3.33));

  const score =
    rsiScore * 0.25 +
    momentumScore * 0.2 +
    volScore * 0.2 +
    volumeTrendScore * 0.15 +
    drawdownScore * 0.2;

  const rounded = Math.round(score);
  let label = "Neutral";
  let color = "#eab308";
  if (rounded <= 20) {
    label = "Extreme Fear";
    color = "#dc2626";
  } else if (rounded <= 40) {
    label = "Fear";
    color = "#f97316";
  } else if (rounded <= 60) {
    label = "Neutral";
    color = "#eab308";
  } else if (rounded <= 80) {
    label = "Greed";
    color = "#84cc16";
  } else {
    label = "Extreme Greed";
    color = "#22c55e";
  }

  return {
    score: rounded,
    label,
    color,
    components: {
      rsi: Math.round(rsiScore),
      momentum: Math.round(momentumScore),
      volatility: Math.round(volScore),
      volumeTrend: Math.round(volumeTrendScore),
      drawdown: Math.round(drawdownScore),
    },
  };
}

// ---------- Optimal Buy Zones ----------
//
// Finds price levels where a confluence of factors suggests a favorable
// entry. Uses:
//   - RSI oversold (<35)
//   - Below lower Bollinger band
//   - Below 50-period EMA
//   - Historical support (volume-weighted)
//
// Returns a ranked list of zones with a confidence score.

export interface BuyZone {
  priceLow: number;
  priceHigh: number;
  confidence: number; // 0-100
  reasons: string[];
}

export function computeBuyZones(data: OHLCV[]): BuyZone[] {
  if (data.length < 50) return [];

  const currentPrice = data[data.length - 1]!.close;
  const bbands = bollingerBands(data, 20, 2);
  const lastBB = bbands[bbands.length - 1];
  const ema50 = ema(data, 50);
  const lastEma50 = ema50[ema50.length - 1]?.value;
  const rsiSeries = rsi(data, 14);
  const lastRsi = rsiSeries[rsiSeries.length - 1]?.value ?? 50;

  const zones: BuyZone[] = [];

  // --- Zone 1: Lower Bollinger Band ---
  if (lastBB) {
    const reasons: string[] = ["Below lower Bollinger Band (2σ)"];
    let confidence = 55;
    if (lastRsi < 35) {
      reasons.push(`RSI oversold (${lastRsi.toFixed(0)})`);
      confidence += 20;
    }
    if (lastEma50 && lastBB.lower < lastEma50) {
      reasons.push("Below 50 EMA support");
      confidence += 10;
    }
    zones.push({
      priceLow: lastBB.lower * 0.98,
      priceHigh: lastBB.lower * 1.02,
      confidence: Math.min(100, confidence),
      reasons,
    });
  }

  // --- Zone 2: Historical volume-weighted support ---
  // Find price buckets in the last 100 bars with highest volume
  const lookback = Math.min(100, data.length);
  const recent = data.slice(-lookback);
  const lows = recent.map((d) => d.low);
  const minLow = Math.min(...lows);
  const maxHigh = Math.max(...recent.map((d) => d.high));
  const bucketCount = 20;
  const bucketSize = (maxHigh - minLow) / bucketCount;
  const buckets = new Array<number>(bucketCount).fill(0);

  for (const bar of recent) {
    const mid = (bar.high + bar.low) / 2;
    const idx = Math.min(
      bucketCount - 1,
      Math.max(0, Math.floor((mid - minLow) / bucketSize)),
    );
    buckets[idx] = (buckets[idx] ?? 0) + bar.volume;
  }

  // Find the highest-volume bucket below current price
  let bestBucket = -1;
  let bestVol = 0;
  for (let i = 0; i < bucketCount; i++) {
    const bucketPrice = minLow + (i + 0.5) * bucketSize;
    if (bucketPrice < currentPrice * 0.95 && (buckets[i] ?? 0) > bestVol) {
      bestVol = buckets[i] ?? 0;
      bestBucket = i;
    }
  }

  if (bestBucket >= 0) {
    const low = minLow + bestBucket * bucketSize;
    const high = low + bucketSize;
    const reasons: string[] = ["High-volume historical support"];
    let confidence = 60;
    if (lastRsi < 40) {
      reasons.push(`RSI approaching oversold (${lastRsi.toFixed(0)})`);
      confidence += 15;
    }
    // Boost if price is approaching this zone
    const distance = ((currentPrice - high) / currentPrice) * 100;
    if (distance < 5 && distance > 0) {
      reasons.push("Price approaching zone");
      confidence += 10;
    }
    zones.push({
      priceLow: low,
      priceHigh: high,
      confidence: Math.min(100, confidence),
      reasons,
    });
  }

  // Deduplicate overlapping zones, sort by confidence
  const merged: BuyZone[] = [];
  for (const z of zones.sort((a, b) => b.confidence - a.confidence)) {
    const overlap = merged.find(
      (m) => !(z.priceHigh < m.priceLow || z.priceLow > m.priceHigh),
    );
    if (!overlap) merged.push(z);
  }
  return merged.slice(0, 3);
}
